import { useEffect, useState } from 'react';
import api from '../../services/api';
import ElectronicWarfareTerminalModal from '../../components/ElectronicWarfareTerminalModal';
import CityScreen from './CityScreen';
import { useCityContext } from './useCityContext';
import {
  ACCESSORY_SLOTS,
  getBackpackCapacity,
  getBatteryMoveTickMs,
  getElectronicWarfareCapabilities,
  getElectronicWarfareProfile,
  ITEM_TYPE_LABELS
} from '../../data/accessories';

function AccessoryCard({ slot, equipped, items, busy, onEquip, onUnequip, onReadMore }) {
  const availableItems = items.filter((item) => Number(item.item_type) === Number(slot.type));
  const hasEquipped = Boolean(equipped);

  return (
    <section className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-cyan-300">{slot.label}</h3>
          <p className="text-xs text-slate-400">{slot.description}</p>
          <button
            type="button"
            onClick={() => onReadMore(slot.key)}
            className="mt-2 text-xs font-semibold text-cyan-200 underline decoration-cyan-400/60 underline-offset-4 hover:text-cyan-100"
          >
            Leer mas
          </button>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className="rounded-full border border-cyan-500/20 bg-[#060c18] px-2.5 py-1 text-[11px] text-cyan-200">
            {slot.statLabel}
          </span>
          <span className={`rounded-full px-2.5 py-1 text-[11px] ${hasEquipped ? 'border border-emerald-400/40 bg-emerald-500/10 text-emerald-200' : 'border border-slate-600 bg-slate-900/70 text-slate-300'}`}>
            {hasEquipped ? 'Activo' : 'Comprado / inactivo'}
          </span>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-slate-700 bg-[#060c18] p-3">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Equipado</div>
        <div className="mt-1 font-semibold text-cyan-100">
          {equipped?.name || 'Sin equipo'}
        </div>
        {equipped && (
          <div className="mt-1 text-xs text-slate-400">
            Tipo: {ITEM_TYPE_LABELS[Number(equipped.item_type)] || `Tipo ${equipped.item_type}`} | Potencia: {equipped.power || 0}
          </div>
        )}
        <button
          type="button"
          disabled={busy || !equipped}
          onClick={() => onUnequip(slot.key)}
          className="mt-3 rounded-lg border border-rose-400/50 px-3 py-2 text-xs text-rose-200 disabled:opacity-40"
        >
          Quitar
        </button>
      </div>

      <div className="mt-4 space-y-2">
        {availableItems.length === 0 && (
          <p className="text-sm text-slate-400">No tienes modelos de este tipo en la mochila.</p>
        )}
        {availableItems.map((item) => {
          const isEquipped = Number(equipped?.id_item || 0) === Number(item.id_item);
          return (
            <div key={`${slot.key}-${item.id}`} className={`rounded-lg border p-3 ${isEquipped ? 'border-cyan-400/60 bg-cyan-500/10' : 'border-slate-700 bg-[#060c18]'}`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="font-semibold text-cyan-100">{item.name} x{item.quantity || 1}</div>
                  <div className="text-xs text-slate-400">
                    {ITEM_TYPE_LABELS[Number(item.item_type)] || `Tipo ${item.item_type}`} | Nivel req: {item.level_required || 0} | Potencia: {item.power || 0}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={busy || isEquipped}
                  onClick={() => onEquip(slot.key, item.id)}
                  className="rounded-md border border-cyan-400/50 px-2.5 py-1.5 text-xs text-cyan-200 disabled:opacity-40"
                >
                  {isEquipped ? 'Activo' : 'Equipar'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function CityHangar() {
  const { loading, error, context, cityTypeLabel, reloadContext } = useCityContext();
  const [inventoryLoading, setInventoryLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [accessories, setAccessories] = useState({});
  const [accessoryItems, setAccessoryItems] = useState([]);
  const [detailSlotKey, setDetailSlotKey] = useState(null);
  const [terminalOpen, setTerminalOpen] = useState(false);
  const blocked = context?.access?.canUseHangar === false;

  async function loadHangar() {
    setInventoryLoading(true);
    try {
      const res = await api.get('/robots/my-inventory');
      setAccessoryItems(res.data?.accessoriesRows || []);
      setAccessories(res.data?.accessories || {});
      if (res.data?.bot || res.data?.summary) {
        // Mantener el contexto de ciudad sincronizado con lo que devuelve inventario.
        await reloadContext(true).catch(() => {});
      }
      setMessage('');
    } finally {
      setInventoryLoading(false);
    }
  }

  function getItemForSlot(slot, inventoryId) {
    const slotType = ACCESSORY_SLOTS.find((entry) => entry.key === slot)?.type;
    const selectedItem = accessoryItems.find((item) => Number(item.id) === Number(inventoryId)) || null;
    if (!selectedItem || Number(selectedItem.item_type) !== Number(slotType)) {
      return null;
    }
    return selectedItem;
  }

  function applyEquipToLocalState(slot, inventoryId) {
    const selectedItem = getItemForSlot(slot, inventoryId);
    if (!selectedItem) return false;

    setAccessoryItems((current) => current.map((item) => {
      if (Number(item.item_type) !== Number(selectedItem.item_type)) return item;
      return {
        ...item,
        equipped: Number(item.id) === Number(selectedItem.id) ? 1 : 0
      };
    }));

    setAccessories((current) => ({
      ...current,
      [slot]: {
        ...selectedItem,
        equipped: 1
      }
    }));

    return true;
  }

  function applyUnequipToLocalState(slot) {
    const slotType = ACCESSORY_SLOTS.find((entry) => entry.key === slot)?.type;
    setAccessoryItems((current) => current.map((item) => {
      if (Number(item.item_type) !== Number(slotType)) return item;
      return {
        ...item,
        equipped: 0
      };
    }));

    setAccessories((current) => ({
      ...current,
      [slot]: null
    }));
  }

  useEffect(() => {
    loadHangar().catch((err) => {
      setMessage(err.response?.data?.error || 'No se pudo cargar el hangar');
    });
  }, []);

  const equippedRadar = accessories.radar || null;
  const equippedBackpack = accessories.backpack || null;
  const equippedBattery = accessories.battery || null;
  const equippedEW = accessories.ewar || null;
  const ewProfile = getElectronicWarfareProfile(equippedEW);
  const activeAccessoryCount = Object.values(accessories).filter(Boolean).length;
  const backpackCapacity = getBackpackCapacity(equippedBackpack);
  const movementTick = getBatteryMoveTickMs(equippedBattery);
  const ewCaps = getElectronicWarfareCapabilities(equippedEW);
  const detailSlot = ACCESSORY_SLOTS.find((slot) => slot.key === detailSlotKey) || null;
  const accessoryStatusLines = ACCESSORY_SLOTS.map((slot) => ({
    key: slot.key,
    label: slot.label.toUpperCase(),
    active: Boolean(accessories[slot.key]),
    itemName: accessories[slot.key]?.name || 'SIN EQUIPO'
  }));

  async function handleEquip(slot, inventoryId) {
    try {
      setBusy(true);
      setMessage('');
      await api.post('/robots/my-inventory/equip', { inventoryId, slot });
      const updated = applyEquipToLocalState(slot, inventoryId);
      if (!updated) {
        await loadHangar();
      }
      await reloadContext(true).catch(() => {});
      setMessage('Loadout actualizado');
    } catch (err) {
      setMessage(err.response?.data?.error || 'No se pudo equipar el accesorio');
    } finally {
      setBusy(false);
    }
  }

  async function handleUnequip(slot) {
    try {
      setBusy(true);
      setMessage('');
      await api.post('/robots/my-inventory/unequip', { slot });
      applyUnequipToLocalState(slot);
      await reloadContext(true).catch(() => {});
      setMessage('Accesorio retirado');
    } catch (err) {
      setMessage(err.response?.data?.error || 'No se pudo retirar el accesorio');
    } finally {
      setBusy(false);
    }
  }

  if (loading || inventoryLoading) {
    return <div className="min-h-screen bg-[#04060d] text-cyan-200 flex items-center justify-center">Cargando hangar...</div>;
  }

  if (error) {
    return <div className="min-h-screen bg-[#04060d] text-rose-200 flex items-center justify-center">{error}</div>;
  }

  return (
    <CityScreen
      context={context}
      cityTypeLabel={cityTypeLabel}
      title={`Hangar - ${context.city?.name || 'Ciudad'}`}
      subtitle="Centro de configuracion y conexion de accesorios"
    >
      {blocked && (
        <section className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
          <p className="text-amber-200 text-sm">Acceso restringido: piloto neutral en ciudad de clan no puede usar Hangar.</p>
        </section>
      )}

      {message && (
        <div className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-3 text-sm text-cyan-200">
          {message}
        </div>
      )}

      <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
          <div className="text-slate-400 text-sm">Radar</div>
          <div className="text-cyan-200 font-bold">{equippedRadar?.name || 'Sin radar'}</div>
          <div className="text-xs text-slate-400">{equippedRadar ? 'Radar activo en el bot' : 'Radar comprado pero apagado o ausente'}</div>
        </div>
        <div className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
          <div className="text-slate-400 text-sm">Mochila</div>
          <div className="text-cyan-200 font-bold">{equippedBackpack?.name || 'Sin mochila'}</div>
          <div className="text-xs text-slate-400">Capacidad: {backpackCapacity} artículos</div>
        </div>
        <div className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
          <div className="text-slate-400 text-sm">Bateria</div>
          <div className="text-cyan-200 font-bold">{equippedBattery?.name || 'Sin bateria'}</div>
          <div className="text-xs text-slate-400">Retardo de paso: {movementTick} ms</div>
        </div>
        <div className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
          <div className="text-slate-400 text-sm">Guerra electronica</div>
          <div className="text-cyan-200 font-bold">{equippedEW?.name || 'Sin modulo'}</div>
          <div className="text-xs text-slate-400">Perfil: {equippedEW ? ewProfile.quality : 'N/A'} | Potencia: {equippedEW ? ewCaps.strength : 0}</div>
          <div className="mt-2 flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.2em]">
            <span className={`rounded-full border px-2 py-1 ${ewCaps.canBypassForeignCities ? 'border-emerald-400/40 text-emerald-200' : 'border-slate-600 text-slate-500'}`}>
              Ciudad
            </span>
            <span className={`rounded-full border px-2 py-1 ${ewCaps.canJamMines ? 'border-cyan-400/40 text-cyan-200' : 'border-slate-600 text-slate-500'}`}>
              Minas
            </span>
            <span className={`rounded-full border px-2 py-1 ${ewCaps.canDisruptEnemies ? 'border-amber-400/40 text-amber-200' : 'border-slate-600 text-slate-500'}`}>
              Enemigos
            </span>
          </div>
          {(!blocked && ewCaps.hasModule) ? (
            <button
              type="button"
              onClick={() => setTerminalOpen(true)}
              className="mt-4 rounded-lg border border-emerald-400/50 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-100 transition hover:bg-emerald-500/20"
            >
              Conectar a terminal
            </button>
          ) : (
            <div className="mt-4 text-xs text-slate-500">
              {blocked ? 'Hangar restringido en esta ciudad.' : 'Terminal bloqueada hasta equipar un modulo de guerra electronica.'}
            </div>
          )}
        </div>
      </section>

      <section className="rounded-2xl border border-cyan-500/30 bg-[#08101f] p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-cyan-300">Loadout tactico</h2>
            <p className="text-sm text-slate-300/80">Aqui conectas los accesorios activos del bot. Las armas siguen en sus slots de combate.</p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-slate-400">
            <span className="rounded-full border border-slate-700 bg-[#060c18] px-3 py-1">
              Comprados: <span className="text-cyan-200 font-bold">{accessoryItems.length}</span>
            </span>
            <span className="rounded-full border border-slate-700 bg-[#060c18] px-3 py-1">
              Activos: <span className="text-emerald-200 font-bold">{activeAccessoryCount}</span>
            </span>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 xl:grid-cols-2 gap-4">
          {ACCESSORY_SLOTS.map((slot) => (
            <AccessoryCard
              key={slot.key}
              slot={slot}
              equipped={{
                radar: equippedRadar,
                backpack: equippedBackpack,
                battery: equippedBattery,
                ewar: equippedEW
              }[slot.key]}
              items={accessoryItems}
              busy={busy}
              onEquip={handleEquip}
              onUnequip={handleUnequip}
              onReadMore={setDetailSlotKey}
            />
          ))}
        </div>

        <div className="mt-5 rounded-2xl border border-cyan-500/30 bg-[#060c18] p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-bold text-cyan-300">Bot de despliegue</h3>
              <p className="text-xs text-slate-400">Estado visual del loadout y lectura rapida de componentes.</p>
            </div>
            <div className="rounded-full border border-cyan-500/20 bg-[#08101f] px-3 py-1 text-xs text-cyan-200">
              {context.bot?.name || 'BOT'}
            </div>
          </div>

          <div className="mt-4 overflow-hidden rounded-2xl border border-cyan-500/20 bg-black">
            <img
              src="/img/bot-despliegue.png"
              alt="Bot de despliegue"
              className="block h-auto w-full object-cover"
            />
            <div className="border-t border-cyan-500/15 bg-[#06101b] px-4 py-3 font-mono text-[12px] leading-6 text-cyan-100">
              <div className="space-y-2">
                {accessoryStatusLines.map((line) => (
                  <div key={line.key} className="flex items-start gap-3">
                    <span className="text-slate-500">&gt;</span>
                    <span className="w-28 shrink-0 text-slate-300">{line.label}</span>
                    <span className={`shrink-0 ${line.active ? 'text-emerald-300' : 'text-rose-300'}`}>
                      [{line.active ? 'OK' : 'NO'}]
                    </span>
                    <span className="text-slate-300">
                      {line.active ? 'ACTIVO' : 'INACTIVO'} - {line.itemName}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 border-t border-cyan-500/10 pt-3 text-slate-400">
                Radar: {equippedRadar ? 'enlace estable' : 'sin señal'} | Mochila: {equippedBackpack ? `${backpackCapacity} art.` : 'sin soporte'} | Bateria: {equippedBattery ? `${movementTick} ms` : 'sin energia'}
              </div>
            </div>
          </div>
        </div>
      </section>
      {detailSlot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6">
          <div className="w-full max-w-2xl rounded-2xl border border-cyan-500/30 bg-[#07101d] p-5 shadow-2xl shadow-cyan-950/40">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.24em] text-cyan-300/70">Descripcion del accesorio</div>
                <h3 className="mt-1 text-2xl font-bold text-cyan-200">{detailSlot.label}</h3>
              </div>
              <button
                type="button"
                onClick={() => setDetailSlotKey(null)}
                className="rounded-lg border border-slate-600 px-3 py-1.5 text-xs text-slate-200 hover:border-cyan-400/60 hover:text-cyan-100"
              >
                Cerrar
              </button>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-slate-700 bg-[#060c18] p-4">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Que hace</div>
                <p className="mt-2 text-sm leading-6 text-slate-200">{detailSlot.details}</p>
              </div>
              <div className="rounded-xl border border-slate-700 bg-[#060c18] p-4">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Como se usa</div>
                <p className="mt-2 text-sm leading-6 text-slate-200">{detailSlot.usage}</p>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-4 text-sm text-cyan-100">
              {detailSlot.description}
            </div>
          </div>
        </div>
      )}
      <ElectronicWarfareTerminalModal
        open={terminalOpen}
        onClose={() => setTerminalOpen(false)}
        botName={context.bot?.name || 'Sin nombre'}
        moduleRow={equippedEW}
      />
    </CityScreen>
  );
}
